import React, { FC, useCallback, useState, memo } from 'react';
import styled from 'styled-components';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import _findIndex from 'lodash/findIndex';
import { gqlMediaTypeCodeEnum as MEDIA_TYPE } from '@minecraft.graphql-types';
import { useTranslation } from '@blocs.i18n';
import CloudinaryVideoAudio from '@minecraft.cloudinary-video-audio';
import CloudinaryMediaViewer from '@minecraft.cloudinary-media-viewer';
import { CoreTheme as theme } from '@minecraft.themes';
import { AUDIO_THUMBNAIL_SRC } from '../../shared/config';
import { MediaType } from '../SummaryTypes';
import { SectionTitleWithIcon, SectionTitleIcon } from '../SummaryStyles';

const ThumbnailWrapper = styled.div`
  cursor: pointer;
`;

const ReelList = styled.div`
  display: flex;
  flex-wrap: wrap;

  ${ThumbnailWrapper} {
    margin-top: 0.625rem;

    &:not(:last-child) {
      margin-right: 1rem;
    }
  }
`;

type Props = {
  reels: ReadonlyArray<MediaType>;
};

const SummaryReels: FC<Props> = ({ reels }) => {
  const { t } = useTranslation();

  const [isMediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [activeMedia, setActiveMedia] = useState(null);

  const handleMediaViewerToggle = useCallback(() => {
    setMediaViewerOpen(!isMediaViewerOpen);
  }, [isMediaViewerOpen]);

  const handleMediaViewerOpen = useCallback(
    (media) => {
      setActiveMedia(media);
      handleMediaViewerToggle();
    },
    [handleMediaViewerToggle]
  );

  const renderThumbnail = useCallback(
    ({ media }): JSX.Element => {
      switch (media?.mediaType?.code) {
        case MEDIA_TYPE.VIDEO:
          return (
            <ThumbnailWrapper key={media.id} onClick={(): void => handleMediaViewerOpen(media)}>
              <CloudinaryVideoAudio
                isThumbnail
                fileKey={media?.fileKey}
                mediaStorageStatus={media?.mediaStorageStatus?.code}
                thumbnailUrl={media?.thumbnailUrl}
                thumbnail={media?.thumbnail}
                mediaType={media?.mediaType?.code}
                mediaStatus={media?.mediaStatus?.code}
                guid={media?.guid}
                type="xsm"
              />
            </ThumbnailWrapper>
          );
        case MEDIA_TYPE.AUDIO:
          return (
            <ThumbnailWrapper key={media.id} onClick={(): void => handleMediaViewerOpen(media)}>
              <img src={AUDIO_THUMBNAIL_SRC} alt={media?.name} />
            </ThumbnailWrapper>
          );
        default:
          return null;
      }
    },
    [handleMediaViewerOpen]
  );

  return (
    !_isEmpty(reels) && (
      <div>
        <SectionTitleWithIcon>
          <SectionTitleIcon name="spicerack-reel" color={theme.colors.primary.darkGrey} />
          {t('common:profile.reels')}
        </SectionTitleWithIcon>
        <ReelList>{_map(reels, (reel) => renderThumbnail(reel))}</ReelList>
        {isMediaViewerOpen && (
          <CloudinaryMediaViewer
            isSmall
            stageName=""
            startIndex={_findIndex(reels, { media: { id: activeMedia.id } })}
            mediaList={reels}
            handleCloseModal={handleMediaViewerToggle}
          />
        )}
      </div>
    )
  );
};

export default memo<Props>(SummaryReels);
